import { useMutation } from 'react-query';
import { Box, VStack } from 'native-base';
import { startCase, toLower } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@pimm/base';
import { ValidatePinRequest } from '@pimm/services/lib/sms-tenants';
import { ValidatePin } from '@pimm/services/lib/sms-tenants/services';
import { EmployeeDto, HubStatDto } from '@pimm/services/lib/sms-workforce';
import { useSiteConfig } from '@app/features/store-core';
import { PinCode } from '@app/components/shared';
import { PinSetupModalType } from '../types';

type FormPasscodeProps = {
  employee?: EmployeeDto;
  team?: HubStatDto;
  learningPathUrl?: string;
  onClose: () => void;
  onPinSuccess?: () => void;
  onOpenModal: (modalName: PinSetupModalType, url?: string) => () => void;
};

export const FormPasscode = ({ learningPathUrl, team, employee, ...props }: FormPasscodeProps) => {
  const { siteConfig } = useSiteConfig();
  const { t } = useTranslation();

  const validatePin = useMutation({
    mutationFn: ValidatePin,
    onSuccess: () => {
      if (team && learningPathUrl) {
        window.open(learningPathUrl, '_blank', 'noreferrer');
        props.onClose();
      } else if (props.onPinSuccess) props.onPinSuccess();
    },
  });

  const handleValidatePin = async (pincode?: string): Promise<void> => {
    const payload: ValidatePinRequest = {
      userId: team ? team?.id! : employee?.id!,
      siteId: siteConfig.id!,
      pin: pincode ?? '',
    };

    await validatePin.mutateAsync(payload);
  };

  return (
    <Box p={6}>
      <PinCode
        headerComponent={
          <VStack w="full">
            <Text size="xl" color="gray.900" fontWeight={700}>
              {t('app_insights:pin_required')}
            </Text>
            <Text size="md" color="black" fontWeight={500}>
              {t('app_insights:enter_pin_prompt')}:
            </Text>
            <Text size="lg" color="gray.900" fontWeight={700}>
              {startCase(toLower([team?.firstName ?? employee?.firstName, team?.lastName ?? employee?.lastName].filter(Boolean).join(' ')))}
            </Text>
          </VStack>
        }
        footerComponent={
          <Button
            variant="unstyled"
            borderWidth={0}
            onPress={props.onOpenModal('pin-setup', learningPathUrl)}
            _text={{ color: 'primary.600', fontWeight: 500 }}
          >
            {t('app_insights:reset_pin_code')}
          </Button>
        }
        submitLabel={t('app_insights:validate')}
        onCancel={props.onClose}
        onValidate={handleValidatePin}
      />
    </Box>
  );
};

export default FormPasscode;
